/*******************************************************
 * Copyright (C) BoostCommerce.net
 *
 * This file is part of commercial BoostCommerce.net projects.
 *
 * This file can not be copied and/or distributed without the express
 * permission of BoostCommerce.net
 *
 * @Date:   2022-09-26 11:39:10
 *
 *******************************************************/
// @flow

import _ from 'lodash';
import React, { Component } from 'react';
import { connect } from 'react-redux';
import { ButtonGroup, Button } from '@shopify/polaris';
import { withI18n } from '@shopify/react-i18n';
import { reduxForm, getFormSyncErrors, getFormValues } from 'redux-form';

import { FullscreenBar } from 'src/components/views/common/fullscreenBar';
import Settings from './settings';
import SettingsHeading from './settings-heading';
import Preview from './preview';
import RouteLeavingGuard from 'src/components/views/common/handle-leave/route-leaving-guard';

import { confirmLeavePage } from 'src/helpers';
import { buildInitialSettingsValues } from 'src/helpers/recommendation-helper';
import history from 'src/routes/history';

import { RECOMMENDATION_WIDGET_RECOMMENDATION_TYPES as RECOMMENDATION_TYPES } from 'src/constants/recommendation';

import * as fontActions from 'actions/font-actions';
import * as recommendationActions from 'actions/recommendation-actions';
import * as themeSettingAction from 'src/stores/actions/theme-settings-actions';
import * as themeSetupAction from 'actions/theme-setup-actions';
import * as quickStartGuideAction from 'actions/dashboard/quick-start-guide-action';

import type {
  CreateRecommendationWidgetType,
  RecommendationWidgetType
} from 'src/models/recommendation';
import type { ComposedProps, State } from './index.type';

class VisualEditor extends Component<ComposedProps, State> {
  state = {
    selectedThemeValue: {},
    themesList: [],
    currentSection: 0,
    syncErrorOnSections: { section_0: false, section_1: false }
  };

  async componentDidMount() {
    const { fontListSuccess, getFonts, allThemes, selectedTheme, getThemes } = this.props;
    if (!fontListSuccess) {
      await getFonts();
    }
    if (!_.isEmpty(selectedTheme)) {
      this.buildThemeList(allThemes, selectedTheme);
    }

    // get current all themes
    await getThemes();

    // Handle when reload or leave page
    window.addEventListener('beforeunload', this.confirmLeavePage);
  }

  componentWillReceiveProps(nextProps) {
    const {
      saving: prevSaving,
      widgetCreating: prevWidgetCreating,
      allThemes,
      currentStep
    } = this.props;
    const {
      saving,
      saveSuccess,
      widgetCreating,
      widgetCreatedSuccess,
      updateState,
      allThemes: nextAllThemes,
      selectedTheme: nextSelectedTheme
    } = nextProps;
    // Update success
    if (
      (prevSaving && !saving && saveSuccess) ||
      (prevWidgetCreating && !widgetCreating && widgetCreatedSuccess)
    ) {
      updateState('currentStep', currentStep + 1);
    }
    if (!_.isEqual(allThemes, nextAllThemes)) {
      this.buildThemeList(nextAllThemes, nextSelectedTheme);
    }
  }

  componentWillUnmount() {
    // Handle when reload or leave page
    window.removeEventListener('beforeunload', this.confirmLeavePage);
  }

  buildThemeList(allThemes, selectedTheme) {
    const themesList = allThemes
      ? allThemes.map(item => ({
          value: item.id,
          label: `${item.name}${
            item.role === 'main'
              ? this.props.i18n.translate('AdminHTML.ThemeSetup.ThemeSetup.suffixThemeLive')
              : ''
          }`,
          theme: item
        }))
      : [];
    const selectedThemeValue = themesList.filter(theme => theme.value === selectedTheme.id)[0];
    this.setState({ themesList, selectedThemeValue });
  }

  async getThemeSetting(selectedTheme) {
    const { getThemeSettingById } = this.props;
    const themeId = selectedTheme.id;
    if (themeId) {
      getThemeSettingById(themeId);
    }
  }

  render() {
    const {
      i18n,
      saving,
      widgetCreating,
      handleSubmit,
      touch,
      widget,
      isCreateFlow,
      currentStep,
      dirty,
      dirtyOnStep,
      handleCloseFullscreenPage
    } = this.props;
    const { themesList, selectedThemeValue, currentSection } = this.state;
    const disabledNextStep = this.disabledNextStep();

    const { recommendationType } = widget;
    const { hasSettings } = RECOMMENDATION_TYPES[recommendationType];
    const title = isCreateFlow
      ? hasSettings
        ? `NewWidgetItem.step3Title`
        : `NewWidgetItem.step2Title`
      : `WidgetItem.title`;

    const shouldConfirmWhenLeave =
      !_.isEmpty(_.filter(dirtyOnStep, (value, key) => key !== `step_${currentStep}` && value)) ||
      dirty;
    return (
      <div className="BC-Page BC-Page__RecommendationWidgetItem">
        <FullscreenBar
          onAction={() => handleCloseFullscreenPage(!shouldConfirmWhenLeave)}
          backTitle={i18n.translate('AdminHTML.Common.Action.exit')}
          title={i18n.translate(`AdminHTML.Recommendation.${title}`)}
          actions={
            <ButtonGroup>
              {isCreateFlow || hasSettings ? (
                <Button onClick={this.handlePrevious} disabled={saving || widgetCreating}>
                  {i18n.translate('AdminHTML.Common.Action.previous')}
                </Button>
              ) : (
                ''
              )}

              <Button
                primary
                onClick={handleSubmit(this.handleSave.bind(this))}
                disabled={disabledNextStep}
                loading={saving || widgetCreating}>
                {i18n.translate('AdminHTML.Recommendation.WidgetItem.nextStep')}
              </Button>
            </ButtonGroup>
          }
          progress={isCreateFlow ? (hasSettings ? 75 : 67) : -1}
        />
        <div className="BC-Widget-Editor-Page widget-visual-editor">
          <div className="visual-editor-body">
            <SettingsHeading
              widget={widget}
              currentSection={currentSection}
              updateCurrentSection={this.updateCurrentSection}
            />
            <Preview
              widget={widget}
              selectedTheme={
                !_.isEmpty(selectedThemeValue) ? selectedThemeValue : this.props.selectedTheme
              }
              themesList={themesList}
              selectedThemeValue={selectedThemeValue}
              handleChangeTheme={this.handleChangeTheme}
            />
            <Settings widget={widget} touch={touch} currentSection={currentSection} />
          </div>
        </div>
        {/* Confirm before leaving */}
        <RouteLeavingGuard
          when={shouldConfirmWhenLeave}
          // Navigate function
          navigate={path => history.push(path)}
          shouldBlockNavigation={shouldConfirmWhenLeave}
          isAvailableFullscreen={true}
        />
      </div>
    );
  }

  // Change section
  updateCurrentSection = (nextCurrentSection: number) => {
    const { syncError } = this.props;
    const { syncErrorOnSections, currentSection } = this.state;
    syncErrorOnSections[`section_${currentSection}`] = !!Object.keys(syncError).length > 0;

    this.setState({ currentSection: nextCurrentSection, syncErrorOnSections });
  };

  // Handle action previous
  handlePrevious = () => {
    const { updateState, currentStep, isCreateFlow, widget, formValues, dirty, dirtyOnStep } =
      this.props;
    const { recommendationType } = widget;
    const { hasSettings } = RECOMMENDATION_TYPES[recommendationType];

    const widgetSettings = this.buildWidgetSettings(widget, formValues);

    dirtyOnStep[`step_${currentStep}`] = dirty;

    updateState('dirtyOnStep', dirtyOnStep);
    updateState('widget', widgetSettings);
    updateState('currentStep', currentStep - (!hasSettings && isCreateFlow ? 2 : 1));
  };

  disabledNextStep = () => {
    const { syncError } = this.props;
    const { syncErrorOnSections, currentSection } = this.state;
    const errorOnAnotherSection = !_.isEmpty(
      _.filter(syncErrorOnSections, (value, key) => key !== `section_${currentSection}` && value)
    );

    return !!Object.keys(syncError).length > 0 || errorOnAnotherSection;
  };

  // handle save settings
  handleSave = data => {
    const {
      isCreateFlow,
      createRecommendationWidget,
      updateRecommendationWidget,
      syncError,
      widget,
      markDoneStep
    } = this.props;
    if (!Object.keys(syncError).length) {
      const widgetSettings = this.buildWidgetSettings(widget, data, true);
      if (isCreateFlow) {
        markDoneStep('V2_RECOMMEND2', {
          isDone: true
        });
        createRecommendationWidget(widgetSettings);
      } else {
        updateRecommendationWidget(widget.id, widgetSettings);
      }
    }
  };

  buildWidgetSettings = (widget, data, isForAPI = false) => {
    const { themePreview, step, ...widgetSettingValue } = widget;
    delete widgetSettingValue._id;
    widgetSettingValue.widgetName = data.widgetName;
    const widgetDesignSettings = {
      widgetId: widget.id,
      tenantId: widget.tenantId,
      layoutDisplay: String(data.layoutDisplay) || '',
      numberOfProductPerRow: data.numberOfProductPerRow || '',
      numberOfRecommendProduct: data.numberOfRecommendProduct || '',
      templateType: data.isDefault ? 'default' : 'customization',
      themePreview: data.themePreview || '',
      titleAlignment: data.titleAlignment || '',
      titleFont: data.titleFont || '',
      titleFontSize: data.titleFontSize || '',
      titleFontStyle: data.titleFontStyle || '',
      titleTextColor: data.titleTextColor || '',
      titleTextTransform: data.titleTextTransform || ''
    };
    if (data.bundleStyle && String(data.layoutDisplay) === 'bundle') {
      widgetDesignSettings.bundleStyle = data.bundleStyle;
      delete widgetDesignSettings.numberOfProductPerRow;
    }
    if (widgetSettingValue.AIBasedRecommendProduct) {
      widgetSettingValue.AIBasedRecommendProduct = String(
        widgetSettingValue.AIBasedRecommendProduct
      );
    }
    if (widgetSettingValue.secondaryAlgorithm) {
      widgetSettingValue.secondaryAlgorithm = String(widgetSettingValue.secondaryAlgorithm);
    }

    const widgetSettings = {
      ...widgetSettingValue,
      widgetDesignSettings,
      step: step === 1 ? 2 : step
    };

    if (widgetSettings.htmlPlaceholder)
      widgetSettings.htmlPlaceholder = encodeURIComponent(widgetSettings.htmlPlaceholder);
    if (isForAPI) {
      // Delete redundant data
      delete widgetSettings.primaryRules;
    }
    return widgetSettings;
  };

  handleChangeTheme = (selectedTheme: any) => {
    this.getThemeSetting(selectedTheme.theme);
    this.setState({ selectedThemeValue: selectedTheme });
  };

  // Handle confirm leave page
  confirmLeavePage = event => {
    const { i18n, dirty } = this.props;
    return confirmLeavePage(event, dirty, i18n);
  };
}

const mapDispatchToProps = (dispatch, ownProps) => ({
  getFonts: () => dispatch(fontActions.getFonts(ownProps.i18n)),
  createRecommendationWidget: (recommendationWidget: CreateRecommendationWidgetType) =>
    dispatch(recommendationActions.createRecommendationWidget(recommendationWidget, ownProps.i18n)),
  updateRecommendationWidget: (widgetId, recommendationWidget: RecommendationWidgetType) =>
    dispatch(
      recommendationActions.updateRecommendationWidget(
        widgetId,
        recommendationWidget,
        {},
        ownProps.i18n
      )
    ),
  getThemes: () => dispatch(themeSetupAction.getThemes(ownProps.i18n)),
  getThemeSettingById: id =>
    dispatch(themeSettingAction.getThemeSettingById(id, ownProps.i18n, 'recommendation')),
  markDoneStep: (stepName, value) =>
    dispatch(quickStartGuideAction.markDoneStep(stepName, value, ownProps.i18n))
});

const mapStateToProps = (state, props) => {
  const { widget } = props;
  const { saving, saveSuccess, widgetCreating, widgetCreatedSuccess } = state.recommendation;
  const allThemes = _.get(state.themeSetupV3, 'allThemes', []);
  const syncError = getFormSyncErrors('VisualEditorForm')(state);
  const formValues = getFormValues('VisualEditorForm')(state);
  const widgetDesignShareableSettings = _.get(
    state.recommendation,
    'widgetDesignShareableSettings',
    {}
  );

  return {
    syncError,
    saving,
    saveSuccess,
    widgetCreating,
    widgetCreatedSuccess,
    allThemes,
    initialValues: buildInitialSettingsValues(widget, widgetDesignShareableSettings || {}),
    formValues
  };
};

const VisualEditorForm = reduxForm({
  form: 'VisualEditorForm', // a unique identifier for this form
  enableReinitialize: true
})(VisualEditor);

export default withI18n()(connect(mapStateToProps, mapDispatchToProps)(VisualEditorForm));
